import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message } from "antd";
import { createCouncil, getAllCouncils, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const CouncilComponent = ({ exception, onClose, onResolved }) => {
    const [councilName, setCouncilName] = useState(null);
    const [councils, setCouncils] = useState([]);
    const [selectedCouncilId, setSelectedCouncilId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const extractCouncilName = (message) => {
            const regex = /Name\s'([^']+)'/;
            const match = message.match(regex);
            setCouncilName(match ? match[1] : null);
        };

        if (exception && exception.description) {
            extractCouncilName(exception.description);
        }

        const fetchCouncils = async () => {
            setLoading(true);
            try {
                const councilsAll = (await getAllCouncils()).data;
                setCouncils(Array.isArray(councilsAll) ? councilsAll : []);
            } catch (error) {
                console.error("Failed to fetch councils", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCouncils();
    }, [exception]);

    const handleCouncilSelect = (value) => {
        setSelectedCouncilId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedCouncilId) return;
        setLoading(true);
        if (exception.status === 'pending') {
            try {
                await createCouncil({ name: councilName, alias_for: selectedCouncilId });
                await updateException(exception.id);
                console.log(`Council "${councilName}" set as alias for council with ID: ${selectedCouncilId}`);
                message.success('Alias saved');
                if (onResolved) await onResolved();
                onClose();
            } catch (error) {
                console.error(`Failed to update alias for council with ID: ${selectedCouncilId}`, error);
                message.error('Failed to save alias');
                onClose();
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
            console.warn("Exception already resolved.");
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                        {councilName ? `Select an existing council to alias "${councilName}" to.` : 'Select an existing council to alias.'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <span style={{ whiteSpace: 'nowrap' }}>Make alias for</span>
                        <Select
                            showSearch
                            allowClear
                            style={{ minWidth: 240 }}
                            placeholder="Select a council"
                            onChange={handleCouncilSelect}
                            value={selectedCouncilId ?? undefined}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            filterSort={(optionA, optionB) =>
                                (optionA?.children ?? '').toLowerCase()
                                    .localeCompare((optionB?.children ?? '').toLowerCase())
                            }
                            disabled={loading}
                        >
                            {councils?.map((council) => (
                                <Option key={council.id} value={council.id}>
                                    {council.name}
                                </Option>
                            ))}
                        </Select>
                        <Button
                            type="primary"
                            onClick={handleSaveAlias}
                            disabled={loading || !selectedCouncilId}
                            loading={loading}
                        >
                            Save
                        </Button>
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default CouncilComponent;
